import * as React from 'react';
import { Component } from 'react';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import Translate from './Translate';
import DocumentMenu from './Document/DocumentMenu';
import Icon from './Icon';
import HTag from './HTag';
import DynamicLink from './DynamicLink';
import { camelCase } from '@kurtosys/ksys-app-template/dist/utils/changeCase';
import { EntityStore, SuperscriptScope } from '../stores';
import { DataStore } from '../stores';
import { DynamicLinkConfig } from '../models/configuration/components/DynamicLinkConfig';
import {
    KeyDocProps,
    KeyDocumentLink,
} from '../models/configuration/components/KeyDocuments';
import { Theme } from '../models/Theme';

const KeyDocumentsWrapper = styled<any, any>(
    'div',
    'keyDocumentsWrapper',
    'keyDocuments'
)`
    ${propStyles};
`;

KeyDocumentsWrapper.defaultProps = {
    margin: '0 0 24px 0',
};

const KeyDocumentsList = styled<any, any>(
    'ul',
    'keyDocumentsList',
    'keyDocuments'
)`
    ${propStyles};
`;

KeyDocumentsList.defaultProps = {
    listStyle: 'none',
    marginY: 'sm',
};

const KeyDocumentsListItem = styled<any, any>(
    'li',
    'keyDocumentsListItem',
    'keyDocuments'
)`
    ${propStyles};
`;

KeyDocumentsListItem.defaultProps = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingY: 'sm',
    borderWidth: '1px',
    borderStyle: 'none none solid none',
    borderColor: 'gray300',
};

const KeyDocumentLinkIcon = styled<any, any>(
    Icon,
    'keyDocumentLinkIcon',
    'keyDocuments'
)`
    ${propStyles};
`;

KeyDocumentLinkIcon.defaultProps = {};

const keyDocumentsLinkDefaultProps = {
    display: 'flex',
    alignItems: 'center',
    marginY: 'md',
    color: 'link',
    fontFamily: 'heading',
    fontWeight: 'bold',
    fontSize: 'xs',
    hover: {
        color: 'linkHover',
    },
};

interface KeyDocumentsProps {
    theme?: Theme;
    keyDocuments: Array<KeyDocProps>;
    showHeading?: boolean;
    dataStore?: DataStore;
    entityStore?: EntityStore;
}

@inject('dataStore', 'entityStore')
@observer
class KeyDocuments extends Component<KeyDocumentsProps, any> {
    @computed
    get keyDocumentsConfig() {
        return this.props.dataStore.config.rawRowConfig.keyDocuments;
    }

    @computed
    get dynamicLinkConfig(): KeyDocumentLink {
        const {
            url,
            linkProperty,
            linkLabel,
            showDynamicLinkIcon,
            linkIconFilename,
        } = this.keyDocumentsConfig;

        return {
            url,
            linkProperty,
            label: linkLabel,
            showIcon: showDynamicLinkIcon,
            iconFilename: linkIconFilename,
        };
    }

    @computed
    get dynamicLinkComponents() {
        const { theme, entityStore } = this.props;
        const { links } = this.keyDocumentsConfig;

        const dynamicLinkComponent = [];
        const workingLinks: Array<KeyDocumentLink> = links || [];

        if (!links) {
            workingLinks.push(this.dynamicLinkConfig);
        }

        for (const link of workingLinks) {
            if (link.label) {
                const dynamicLinkConfig: DynamicLinkConfig = {
                    url: link.url,
                    linkProperty: link.linkProperty,
                };

                const key = `keyDocuments-${camelCase(
                    entityStore.value.clientCode
                )}-${link.label}`;

                dynamicLinkComponent.push(
                    <DynamicLink
                        key={key}
                        config={dynamicLinkConfig}
                        queryContext={entityStore.queryContext}
                        metricContext={key}
                        ariaLabelKey={'keyDocumentsLink'}
                        onClick={this.onClick}
                        {...keyDocumentsLinkDefaultProps}
                        {...(theme.KeyDocumentsLink || theme.FundCardLink)}
                    >
                        <Translate>{link.label}</Translate>
                        {link.showIcon && (
                            <KeyDocumentLinkIcon
                                fileName={link.iconFilename || 'DocumentIcon'}
                                height="1.4em"
                                width="1.4em"
                                {...theme.KeyDocumentLinkIcon}
                            />
                        )}
                    </DynamicLink>
                );
            }
        }

        return dynamicLinkComponent;
    }

    getDocuments = keyDocuments => {
        const {
            showSingleDocument = false,
            showSingleDocumentLabel = false,
            showSingleDocumentGroupLabel = true,
            showIcon,
        } = this.keyDocumentsConfig;
        const { documents, documentGroupName } = keyDocuments;
        // explicit check to deal with undefined and default to show
        const showDocumentIcon = showIcon !== false;
        return (
            <DocumentMenu
                documents={documents}
                superscriptScope={SuperscriptScope.module}
                documentGroupLabel={documentGroupName}
                showSingleDocument={showSingleDocument}
                showSingleDocumentLabel={showSingleDocumentLabel}
                showSingleDocumentGroupLabel={showSingleDocumentGroupLabel}
                metricContext={`keyDocuments-${camelCase(documentGroupName)}`}
            >
                {showDocumentIcon && (
                    <Icon
                        fileName="DocumentIcon"
                        height="1.4em"
                        width="1.4em"
                    />
                )}
            </DocumentMenu>
        );
    };

    onClick = (link: string) => {
        const { dataStore } = this.props;
        const { links } = this.keyDocumentsConfig;

        // TODO: How should multi url work with the datalayer event key
        dataStore.logDataLayer(
            links ? 'key_documents_urls' : 'view_all_documents',
            {
                url: link,
            },
            'click'
        );
    };

    render() {
        const { theme, showHeading, keyDocuments } = this.props;
        return (
            <KeyDocumentsWrapper {...theme.KeyDocumentsWrapper}>
                {showHeading && (
                    <HTag
                        componentTheme={'KeyDocumentsHeading'}
                        defaultStyleProps={{
                            color: 'body',
                            fontFamily: 'heading',
                            fontWeight: 'bold',
                            fontSize: 'md',
                        }}
                        level={2}
                        text={this.keyDocumentsConfig.label}
                    />
                )}
                <KeyDocumentsList {...theme.KeyDocumentsList}>
                    {keyDocuments.map(keyDocument => (
                        <KeyDocumentsListItem
                            key={keyDocument.documentGroupName}
                            {...theme.KeyDocumentsListItem}
                        >
                            {this.getDocuments(keyDocument)}
                        </KeyDocumentsListItem>
                    ))}
                </KeyDocumentsList>
                {this.dynamicLinkComponents}
            </KeyDocumentsWrapper>
        );
    }
}

export default withTheme(KeyDocuments) as any;



// WEBPACK FOOTER //
// ./src/Components/KeyDocuments.tsx